const defaultAfterUrl = (attachment) => attachment?.allegato?.url || null;

const buildFlagWhere = (flag) => ({
  [flag]: { equalTo: true },
});

const buildAiEditPromptWhere = ({
  prompts = [],
  matchesRegex = null,
}) => {
  const promptConditions = [];

  if (prompts.length === 1) {
    promptConditions.push({
      aiEditPrompt: { equalTo: prompts[0] },
    });
  } else if (prompts.length > 1) {
    promptConditions.push({
      aiEditPrompt: { in: prompts },
    });
  }

  if (matchesRegex) {
    promptConditions.push({
      aiEditPrompt: { matchesRegex },
    });
  }

  const promptCondition = promptConditions.length === 1
    ? promptConditions[0]
    : { OR: promptConditions };

  return {
    AND: [
      buildFlagWhere("isAiEdit"),
      promptCondition,
    ],
  };
};

const matchesAiEditPrompt = (
  attachment,
  { prompts = [], pattern = null }
) => {
  if (!attachment?.isAiEdit || typeof attachment.aiEditPrompt !== "string") {
    return false;
  }

  return (
    prompts.includes(attachment.aiEditPrompt) ||
    Boolean(pattern?.test(attachment.aiEditPrompt))
  );
};

const defaultAiEditBeforeUrl = (attachment) =>
  attachment?.aiEditRawImage || null;

// Giotto persists prompt-based tools as isAiEdit + aiEditPrompt instead of
// dedicated Boolean flags. Keep these IDs aligned with aiFeatureCategories.js
// in Giotto and the prompt keys aligned with lib/ai-models/features.js in
// lambda-giotto-api.
const AI_EDIT_RESULT_TYPE_DEFINITIONS = [
  {
    slug: "dissolvenza",
    label: "Dissolvenza",
    promptMatcher: {
      matchesRegex: "^DISSOLVENZA(?:_|$)",
      pattern: /^DISSOLVENZA(?:_|$)/,
    },
  },
  {
    slug: "restore-photo",
    label: "Restaura Foto",
    promptMatcher: {
      prompts: ["RESTORE_PHOTO"],
    },
  },
  {
    slug: "remove-document-photo-marks",
    label: "Ripulisci Foto Documento",
    promptMatcher: {
      prompts: ["RESTORE_ID_PHOTO"],
    },
  },
  {
    slug: "remove-hug",
    label: "Rimuovi Abbraccio",
    promptMatcher: {
      prompts: ["REMOVE_HUG"],
    },
  },
  {
    slug: "remove-the-person-in-front-of-the-subject",
    label: "Rimuovi la persona davanti al soggetto",
    promptMatcher: {
      prompts: ["REMOVE_PERSON_IN_FRONT_OF_SUBJECT"],
    },
  },
  {
    slug: "remove-the-object-in-front-of-the-subject",
    label: "Rimuovi gli oggetti davanti al soggetto",
    promptMatcher: {
      prompts: [
        "REMOVE_OBJECT_IN_FRONT_OF_SUBJECT",
        "REMOVE_OBJECT_IN_FRONT_OF_SUBJECT_ACCURATE",
      ],
    },
  },
  {
    slug: "remove-glasses",
    label: "Rimuovi Occhiali",
    promptMatcher: {
      prompts: ["REMOVE_GLASSES"],
    },
  },
  {
    slug: "remove-glasses-glare",
    label: "Rimuovi Riflesso dagli Occhiali",
    promptMatcher: {
      prompts: ["REMOVE_GLASSES_GLARE"],
    },
  },
  {
    slug: "correct-posture",
    label: "Correggi postura",
    promptMatcher: {
      prompts: ["CORRECT_POSTURE"],
    },
  },
];

const AI_EDIT_RESULT_TYPES = AI_EDIT_RESULT_TYPE_DEFINITIONS.map((type) => ({
  slug: type.slug,
  label: type.label,
  where: buildAiEditPromptWhere(type.promptMatcher),
  matches: (attachment) =>
    matchesAiEditPrompt(attachment, type.promptMatcher),
  getBeforeUrl: defaultAiEditBeforeUrl,
  getAfterUrl: defaultAfterUrl,
}));

const CHANGE_CLOTHES_AI_EDIT_PROMPT_MATCHER = {
  matchesRegex: "^Change the clothes of the subject to",
  pattern: /^Change the clothes of the subject to/,
};

const KNOWN_AI_EDIT_PROMPT_MATCHERS = [
  CHANGE_CLOTHES_AI_EDIT_PROMPT_MATCHER,
  ...AI_EDIT_RESULT_TYPE_DEFINITIONS.map((type) => type.promptMatcher),
];

const isKnownAiEditPrompt = (attachment) =>
  KNOWN_AI_EDIT_PROMPT_MATCHERS.some((promptMatcher) =>
    matchesAiEditPrompt(attachment, promptMatcher)
  );

export const AI_RESULT_TYPES = [
  {
    slug: "removebg",
    label: "Rimuovi Sfondo",
    flag: "isRemovebg",
    where: {
      AND: [
        { isRemovebg: { equalTo: true } },
        { isBlurBg: { equalTo: false } },
      ],
    },
    matches: (attachment) =>
      Boolean(attachment?.isRemovebg) && attachment?.isBlurBg === false,
    getBeforeUrl: (attachment) => attachment?.removebgRawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "blurbg",
    label: "Sfoca Sfondo",
    flag: "isBlurBg",
    where: buildFlagWhere("isBlurBg"),
    matches: (attachment) => Boolean(attachment?.isBlurBg),
    getBeforeUrl: (attachment) => attachment?.removebgRawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "image-enhancement",
    label: "Migliora Foto",
    flag: "isImageEnhancement",
    where: buildFlagWhere("isImageEnhancement"),
    matches: (attachment) => Boolean(attachment?.isImageEnhancement),
    getBeforeUrl: (attachment) => attachment?.enhancementRawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "expand",
    label: "Espandi Scena",
    flag: "isExpand",
    where: buildFlagWhere("isExpand"),
    matches: (attachment) => Boolean(attachment?.isExpand),
    getBeforeUrl: (attachment) => attachment?.expandRawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "magic-brush",
    label: "Pennello Correttivo",
    flag: "isMagicEraser",
    where: buildFlagWhere("isMagicEraser"),
    matches: (attachment) => Boolean(attachment?.isMagicEraser),
    getBeforeUrl: (attachment) => attachment?.magicEraserRawImage || null,
    getAfterUrl: defaultAfterUrl,
    getExtraLayerUrl: (attachment) => attachment?.magicEraserMask || null,
    extraLayerLabel: "Maschera",
  },
  {
    slug: "change-clothes",
    label: "Cambia abito",
    flag: "isChangeClothes",
    where: {
      OR: [
        buildFlagWhere("isChangeClothes"),
        buildAiEditPromptWhere(CHANGE_CLOTHES_AI_EDIT_PROMPT_MATCHER),
      ],
    },
    matches: (attachment) =>
      Boolean(attachment?.isChangeClothes) ||
      matchesAiEditPrompt(
        attachment,
        CHANGE_CLOTHES_AI_EDIT_PROMPT_MATCHER
      ),
    getBeforeUrl: (attachment) =>
      attachment?.changeClothesRawImage ||
      defaultAiEditBeforeUrl(attachment),
    getAfterUrl: defaultAfterUrl,
    getExtraLayerUrl: (attachment) => attachment?.changeClothesMask || null,
    extraLayerLabel: "Maschera",
  },
  {
    slug: "smile",
    label: "Sorriso",
    flag: "isSmile",
    where: buildFlagWhere("isSmile"),
    matches: (attachment) => Boolean(attachment?.isSmile),
    getBeforeUrl: (attachment) => attachment?.smileRawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "colorize-bw",
    label: "Colora B/W",
    flag: "isColorizeBw",
    where: buildFlagWhere("isColorizeBw"),
    matches: (attachment) => Boolean(attachment?.isColorizeBw),
    getBeforeUrl: (attachment) => attachment?.colorizeBwRawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "auto-color",
    label: "Correggi Colore",
    flag: "isAutoColor",
    where: buildFlagWhere("isAutoColor"),
    matches: (attachment) => Boolean(attachment?.isAutoColor),
    getBeforeUrl: (attachment) => attachment?.rawImage || null,
    getAfterUrl: defaultAfterUrl,
  },
  {
    slug: "background-fusion",
    label: "Fusione Sfondo",
    flag: "isBackgroundFusion",
    where: buildFlagWhere("isBackgroundFusion"),
    matches: (attachment) => Boolean(attachment?.isBackgroundFusion),
    getBeforeUrl: (attachment) =>
      attachment?.backgroundFusionRawBackground || null,
    getAfterUrl: (attachment) =>
      attachment?.backgroundFusionComposite || defaultAfterUrl(attachment),
    getExtraLayerUrl: (attachment) =>
      attachment?.backgroundFusionRawSubject || null,
    extraLayerLabel: "Soggetto",
  },
  ...AI_EDIT_RESULT_TYPES,
  {
    slug: "ai-edit",
    label: "Fotoritocco Intelligente",
    flag: "isAiEdit",
    where: buildFlagWhere("isAiEdit"),
    matches: (attachment) =>
      Boolean(attachment?.isAiEdit) &&
      !isKnownAiEditPrompt(attachment),
    getBeforeUrl: defaultAiEditBeforeUrl,
    getAfterUrl: defaultAfterUrl,
  },
];

export const AI_RESULT_TYPE_BY_SLUG = AI_RESULT_TYPES.reduce(
  (typesBySlug, type) => ({ ...typesBySlug, [type.slug]: type }),
  {}
);

const isKnownAiTypeSlug = (slug) =>
  Object.prototype.hasOwnProperty.call(AI_RESULT_TYPE_BY_SLUG, slug);

export const AI_FLAG_LABELS = AI_RESULT_TYPES.reduce(
  (labels, type) => (
    type.flag ? { ...labels, [type.flag]: type.label } : labels
  ),
  {}
);

export const getAttachmentAiTypes = (attachment) =>
  AI_RESULT_TYPES.filter((type) => type.matches(attachment));

export const getAttachmentPresentation = (attachment) => {
  const types = getAttachmentAiTypes(attachment);
  const primaryType = types[0] || null;

  return {
    types,
    primaryType,
    beforeUrl: primaryType?.getBeforeUrl(attachment) || null,
    afterUrl: primaryType?.getAfterUrl(attachment) || null,
    extraLayerUrl: primaryType?.getExtraLayerUrl?.(attachment) || null,
    extraLayerLabel: primaryType?.extraLayerLabel || "Maschera",
  };
};

export const normalizeAiTypeSlugs = (typeSlugs = []) => {
  const requested = new Set(typeSlugs);
  return AI_RESULT_TYPES
    .map((type) => type.slug)
    .filter((slug) => requested.has(slug));
};

export const parseAiTypesParam = (value) => {
  if (value === undefined || value === null || value === "") {
    return { typeSlugs: [], invalidTypeSlugs: [] };
  }

  const rawValues = Array.isArray(value) ? value : [value];
  const requested = Array.from(
    new Set(
      rawValues
        .flatMap((entry) => String(entry).split(","))
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
  const invalidTypeSlugs = requested.filter(
    (slug) => !isKnownAiTypeSlug(slug)
  );

  return {
    typeSlugs: normalizeAiTypeSlugs(requested),
    invalidTypeSlugs,
  };
};

export const serializeAiTypesParam = (typeSlugs = []) => {
  const normalized = normalizeAiTypeSlugs(typeSlugs);
  if (normalized.length === 0 || normalized.length === AI_RESULT_TYPES.length) {
    return undefined;
  }
  return normalized.join(",");
};

export const buildAiResultsWhere = ({ typeSlugs = [], orgId = null } = {}) => {
  const normalized = normalizeAiTypeSlugs(typeSlugs);

  if (typeSlugs.length > 0 && normalized.length !== new Set(typeSlugs).size) {
    throw new Error("Unsupported Giotto AI result type");
  }

  const selectedTypes = normalized.length > 0
    ? normalized.map((slug) => AI_RESULT_TYPE_BY_SLUG[slug])
    : AI_RESULT_TYPES;
  const aiCondition = { OR: selectedTypes.map((type) => type.where) };

  if (!orgId) {
    return aiCondition;
  }

  return {
    AND: [
      {
        org_pointer: {
          have: {
            objectId: { equalTo: orgId },
          },
        },
      },
      aiCondition,
    ],
  };
};
